import { createContext, forwardRef, useContext, type FC, type HTMLAttributes, type MouseEvent } from 'react'
import styles from './toggle-button.module.css'

interface ToggleButtonGroupContext {
  value?: string | string[]
  exclusive?: boolean
  onChange?: (event: MouseEvent<HTMLButtonElement>, value: string | string[]) => void
  size?: 'small' | 'medium' | 'large'
}

const ToggleGroupCtx = createContext<ToggleButtonGroupContext>({})

export interface ToggleButtonGroupProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value?: string | string[]
  exclusive?: boolean
  onChange?: (event: MouseEvent<HTMLButtonElement>, value: string | string[]) => void
  size?: 'small' | 'medium' | 'large'
  orientation?: 'horizontal' | 'vertical'
}

export const ToggleButtonGroup: FC<ToggleButtonGroupProps> = ({
  value,
  exclusive,
  onChange,
  size = 'medium',
  orientation = 'horizontal',
  className,
  ...rest
}) => {
  const classes = [
    styles.group,
    orientation === 'vertical' ? styles.groupVertical : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <ToggleGroupCtx value={{ value, exclusive, onChange, size }}>
      <div role="group" className={classes} {...rest} />
    </ToggleGroupCtx>
  )
}

export interface ToggleButtonProps extends Omit<HTMLAttributes<HTMLButtonElement>, 'value'> {
  value: string
  selected?: boolean
  disabled?: boolean
  size?: 'small' | 'medium' | 'large'
}

const sizeMap: Record<string, string | undefined> = {
  small: styles.small,
  large: styles.large,
}

export const ToggleButton = forwardRef<HTMLButtonElement, ToggleButtonProps>(
  ({ value, selected: selectedProp, disabled, size: sizeProp, className, onClick, ...rest }, ref) => {
    const group = useContext(ToggleGroupCtx)
    const size = sizeProp ?? group.size ?? 'medium'

    const isSelected =
      selectedProp ??
      (group.value != null
        ? Array.isArray(group.value)
          ? group.value.includes(value)
          : group.value === value
        : false)

    const handleClick = (ev: MouseEvent<HTMLButtonElement>) => {
      onClick?.(ev)
      if (group.onChange) {
        if (group.exclusive) {
          group.onChange(ev, value)
        } else if (Array.isArray(group.value)) {
          const next = isSelected ? group.value.filter((v) => v !== value) : [...group.value, value]
          group.onChange(ev, next)
        } else {
          group.onChange(ev, value)
        }
      }
    }

    const classes = [
      styles.toggleButton,
      isSelected ? styles.selected : undefined,
      sizeMap[size],
      className,
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <button
        ref={ref}
        type="button"
        className={classes}
        aria-pressed={isSelected}
        disabled={disabled}
        onClick={handleClick}
        {...rest}
      />
    )
  }
)

ToggleButton.displayName = 'ToggleButton'
